import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useInvoices} from '../hooks/useInvoices';
import {useInvoicesSelection} from '../hooks/useInvoicesSelection';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/InvoicesService.ts';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {Invoice} from '../types';
import {useQueryClient} from '@tanstack/react-query';

export const RecordsTable = ({customerId, subscriptionId}: {customerId?: string, subscriptionId?: string} = {}) => {
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Invoice | null>(null);
    const queryClient = useQueryClient();

    const handleRecordClick = (record: Invoice) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const {columns} = useTableColumns('customer', handleRecordClick);

    const {
        items,
        loading,
        totalCount,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useInvoices('customer', customerId, subscriptionId);

    const {selectedRecords, setSelectedRecords} = useInvoicesSelection();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleDrawerSubmit = async (values: Invoice) => {
        if (selectedRecord?.id) {
            const {customer: _customer, createdByUsername: _createdByUsername, createdBy: _createdBy, created: _created, updated: _updated, ...rest} = values;
            await update(selectedRecord.id, rest as Invoice);
            queryClient.invalidateQueries({queryKey: ['invoices']});
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination loadedCount={totalCount} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            {drawerOpened && selectedRecord && (
                <FormDrawer
                    record={selectedRecord}
                    handleSubmit={handleDrawerSubmit}
                    opened={drawerOpened}
                    onClose={() => {
                        setDrawerOpened(false);
                        setSelectedRecord(null);
                    }}
                />
            )}
        </SimpleGrid>
    );
};
